import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  MedidaName,
  OrderedMedidas,
  Selectable,
  Temporada,
} from '../../../../../../common/context/reducers/selectorReducer'
import { MatriculaMedidasItem } from '../../../../../../common/types/MedidasPorMatricula'
import {
  extractMatriculaMeasureOptions,
  findMatriculaMeasureIdForCatalogSelection,
  findMatriculaMeasureIdForSelection,
  findNextMedidaToComplete,
  hasCompleteMedidasSelection,
  isCompletingHomologationForIncompleteAmiTyre,
  selectedMedidasArePartial,
  selectedMedidasMatchSomeMatriculaTyreForVehicleWarning,
} from './medidasPorMatricula.utils'
import { PageMedida } from '../../../searchNeumaticos.store'

export interface SearchNeumaticosMedidasStateType {
  selectedMedidas: { [k in MedidaName]?: string | number | null }
  previousSelectedMedidas: { [k in MedidaName]?: string | number | null }
  pageMedida: {  ancho?: number , serie?: number,  llanta?: number} | null

  medidasData: {
    [k in MedidaName]?: Selectable<number | string>
  }
  isMedidasConfirmed: boolean
  selectedEstacion: Temporada | null

  disabledEstaciones: Temporada[]
  /** L'usuari ha triat temporada manualment al catàleg (no auto-ajust). */
  estacionSeleccionadaPorUsuario: boolean
  /** L'usuari ha desmarcat la marca al sidebar; no tornar a injectar-la del funnel. */
  marcaFilterOptOut: boolean
  /** Ja s'ha fet l'auto-ajust de temporada en aquesta entrada al catàleg. */
  estacionAutoAjustada: boolean
  matriculaMedidas: MatriculaMedidasItem[]
  matriculaMedidasLoading: boolean
  matriculaMedidasError: string | null
  /** Dins del modal «Buscar por medidas»: no posar `isMedidasConfirmed` automàticament en completar mides. */
  deferMedidasCatalogConfirmUntilApply: boolean
  /** Després de /tyres-with-values amb mides parcials: obrir el següent input (modal mòbil / select desktop). */
  pendingAutoFocusNextMedida: boolean
  /** L'usuari ha canviat mides als selectors del catàleg (no selecció inicial matrícula). */
  medidasAlteredOnCatalog: boolean
  /** Opció AMI triada al modal GridLlantas (no inferir des del primer pneumàtic auto-aplicat). */
  selectedMatriculaMeasureId: string | null
  /** Mida AMI del vehicle incompleta (sense tots els camps): no mostrar avís de modificació. */
  vehicleTyreSelectionWasPartial: boolean
}

export const initialState: SearchNeumaticosMedidasStateType = {
  selectedMedidas: {
    ancho: null,
    serie: null,
    llanta: null,
    carga: null,
    velocidad: null,
  },
  isMedidasConfirmed: true,
  previousSelectedMedidas: {
    ancho: null,
    serie: null,
    llanta: null,
    carga: null,
    velocidad: null,
  },
  pageMedida: null,

  medidasData: {
    ancho: { loading: false, value: null, default: null, options: [] },
    serie: { loading: false, value: null, default: null, options: [] },
    llanta: { loading: false, value: null, default: null, options: [] },
    carga: { loading: false, value: null, default: null, options: [] },
    velocidad: { loading: false, value: null, default: null, options: [] },
  },

  selectedEstacion: null,

  disabledEstaciones: [],
  estacionSeleccionadaPorUsuario: false,
  marcaFilterOptOut: false,
  estacionAutoAjustada: false,
  matriculaMedidas: [],
  matriculaMedidasLoading: false,
  matriculaMedidasError: null,
  deferMedidasCatalogConfirmUntilApply: false,
  pendingAutoFocusNextMedida: false,
  medidasAlteredOnCatalog: false,
  selectedMatriculaMeasureId: null,
  vehicleTyreSelectionWasPartial: false,
}

export const key = 'searchNeumaticos/Medidas'

function syncIsMedidasConfirmedWithSelection(
  state: SearchNeumaticosMedidasStateType
) {
  state.isMedidasConfirmed = OrderedMedidas.every((k) => {
    const v = state.selectedMedidas[k]
    return v != null && v !== ''
  }) || state.pageMedida != null
}

function applyMedidasAlteredOnCatalogWithVehicleSync(
  state: SearchNeumaticosMedidasStateType
) {
  if (!state.matriculaMedidas.length) return
  if (!hasCompleteMedidasSelection(state.selectedMedidas)) return

  const measureOptions = extractMatriculaMeasureOptions(state.matriculaMedidas)
  const tyres = measureOptions.map((option) => option.tyre)
  const matchesVehicle = selectedMedidasMatchSomeMatriculaTyreForVehicleWarning(
    state.selectedMedidas,
    tyres
  )
  const completingIncompleteAmi = isCompletingHomologationForIncompleteAmiTyre(
    state.selectedMedidas,
    state.matriculaMedidas
  )

  state.medidasAlteredOnCatalog =
    state.vehicleTyreSelectionWasPartial || completingIncompleteAmi
      ? false
      : !matchesVehicle
  state.selectedMatriculaMeasureId = matchesVehicle
    ? findMatriculaMeasureIdForCatalogSelection(
        measureOptions,
        state.selectedMedidas
      )
    : null
}

function syncStateAfterCatalogMedidasRestore(
  state: SearchNeumaticosMedidasStateType
) {
  state.isMedidasConfirmed = hasCompleteMedidasSelection(state.selectedMedidas, state.pageMedida)
  applyMedidasAlteredOnCatalogWithVehicleSync(state)
}

function syncMedidasAlteredOnCatalogWithSelection(
  state: SearchNeumaticosMedidasStateType
) {
  if (!state.medidasAlteredOnCatalog || !state.matriculaMedidas.length) return
  if (!hasCompleteMedidasSelection(state.selectedMedidas)) return

  const measureOptions = extractMatriculaMeasureOptions(state.matriculaMedidas)
  const tyres = measureOptions.map((option) => option.tyre)
  if (
    !selectedMedidasMatchSomeMatriculaTyreForVehicleWarning(
      state.selectedMedidas,
      tyres
    )
  ) {
    return
  }

  state.medidasAlteredOnCatalog = false
  state.selectedMatriculaMeasureId = findMatriculaMeasureIdForCatalogSelection(
    measureOptions,
    state.selectedMedidas
  )
}

function applyPreviousMedidasToState(state: SearchNeumaticosMedidasStateType) {
  state.selectedMedidas = { ...state.previousSelectedMedidas }

  for (const name of OrderedMedidas) {
    const prevVal = state.previousSelectedMedidas[name]
    const hasValue = prevVal !== null && prevVal !== undefined && prevVal !== ''
    const prevOptions = state.medidasData[name]?.options ?? []
    const valueInOptions =
      hasValue &&
      prevOptions.some((o) => String(o.value) === String(prevVal))

    state.medidasData[name] = {
      ...state.medidasData[name],
      value: hasValue ? prevVal : null,
      // Evita "Seleccionar" quan el valor restaurat no és a les opcions de la
      // combo parcial que s'estava editant (p.ex. click fora a llanta).
      options: hasValue
        ? valueInOptions
          ? prevOptions
          : [{ label: String(prevVal), value: prevVal }, ...prevOptions]
        : [],
      loading: false,
    }
  }
}

const medidasSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setPreviousSelectedMedidasSnapshot: (state) => {
      state.previousSelectedMedidas = { ...state.selectedMedidas }
      state.estacionSeleccionadaPorUsuario = false
      state.estacionAutoAjustada = false
    },
    setMedidasAlteredOnCatalog: (state, action: PayloadAction<boolean>) => {
      state.medidasAlteredOnCatalog = action.payload
    },
    setVehicleTyreSelectionWasPartial: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.vehicleTyreSelectionWasPartial = action.payload
    },
    updateMedidaSeleccionada: (
      state,
      action: PayloadAction<{ name: MedidaName; value: string | number | null }>
    ) => {
      const { name, value } = action.payload

      const updated = { ...state.selectedMedidas }
      updated[name] = value

      const index = OrderedMedidas.indexOf(name)

      for (let i = index + 1; i < OrderedMedidas.length; i++) {
        updated[OrderedMedidas[i]] = null
      }

      state.selectedMedidas = updated
      state.selectedMatriculaMeasureId = null

      state.medidasData[name] = {
        ...state.medidasData[name],
        value,
      }

      for (let i = index + 1; i < OrderedMedidas.length; i++) {
        const nextName = OrderedMedidas[i]

        state.medidasData[nextName] = {
          ...state.medidasData[nextName],
          value: null,
          options: [],
          loading: true,
        }
      }

      if (!state.deferMedidasCatalogConfirmUntilApply) {
        syncIsMedidasConfirmedWithSelection(state)
      }

      if (state.matriculaMedidas.length) {
        if (hasCompleteMedidasSelection(state.selectedMedidas)) {
          applyMedidasAlteredOnCatalogWithVehicleSync(state)
        } else {
          state.selectedMatriculaMeasureId = null
        }
      } else {
        syncMedidasAlteredOnCatalogWithSelection(state)
      }
    },

    setEstacionSeleccionada: (
      state,
      action: PayloadAction<Temporada | null>
    ) => {
      state.selectedEstacion = action.payload
    },

    setEstacionSeleccionadaPorUsuario: (
      state,
      action: PayloadAction<Temporada | null>
    ) => {
      state.selectedEstacion = action.payload
      state.estacionSeleccionadaPorUsuario = true
    },

    setEstacionAutoAjustada: (state, action: PayloadAction<boolean>) => {
      state.estacionAutoAjustada = action.payload
    },

    setMarcaFilterOptOut: (state, action: PayloadAction<boolean>) => {
      state.marcaFilterOptOut = action.payload
    },

    resetEstacionCatalogState: (state) => {
      state.selectedEstacion = null
      state.estacionSeleccionadaPorUsuario = false
      state.estacionAutoAjustada = false
    },

    resetMedidasSeleccionadas: (state) => {
      state.previousSelectedMedidas = { ...state.selectedMedidas }
      state.selectedMedidas = { ...initialState.selectedMedidas }

      for (const name of OrderedMedidas) {
        state.medidasData[name] = {
          ...state.medidasData[name],
          value: null,
        }
      }
      state.pageMedida = null
      state.selectedEstacion = null
      state.estacionSeleccionadaPorUsuario = false
      state.estacionAutoAjustada = false
      state.vehicleTyreSelectionWasPartial = false
      state.marcaFilterOptOut = false
    },

    setMedidasIniciales: (
      state,
      action: PayloadAction<{ [k in MedidaName]?: string | number | null }>
    ) => {
      state.selectedMedidas = { ...action.payload }
      state.pageMedida = null      
      state.vehicleTyreSelectionWasPartial =
        selectedMedidasArePartial(action.payload)

      const isPartial = selectedMedidasArePartial(action.payload)
      const nextToComplete = isPartial
        ? findNextMedidaToComplete(action.payload)
        : null

      for (const name of OrderedMedidas) {
        const selected = action.payload[name]
        const hasValue =
          selected !== null && selected !== undefined && selected !== ''
        const oldOptions = state.medidasData[name]?.options ?? []
        const valueInOldOptions =
          hasValue &&
          oldOptions.some((o) => String(o.value) === String(selected))

        state.medidasData[name] = {
          ...state.medidasData[name],
          value: selected ?? null,
          // Injecta el valor com a opció temporal si no és a les options
          // antigues, per evitar el «value not in options» mentre es carreguen
          // les noves opcions reals.
          options: hasValue
            ? valueInOldOptions
              ? oldOptions
              : [{ label: String(selected), value: selected }, ...oldOptions]
            : [],
          loading: isPartial && name === nextToComplete,
        }
      }

      if (!state.deferMedidasCatalogConfirmUntilApply) {
        syncIsMedidasConfirmedWithSelection(state)
      }

      syncMedidasAlteredOnCatalogWithSelection(state)
      applyMedidasAlteredOnCatalogWithVehicleSync(state)
    },

    setPageMedidas: (
      state,
      action: PayloadAction< PageMedida | null>
    ) => {
      state.pageMedida = action.payload
      state.selectedMedidas = { ...state.selectedMedidas, ...action.payload, carga: null, velocidad: null }
      state.isMedidasConfirmed = true
      state.pendingAutoFocusNextMedida = false
    },

    setMedidaLoading: (
      state,
      action: PayloadAction<{ name: MedidaName; loading: boolean }>
    ) => {
      const { name, loading } = action.payload
      state.medidasData[name] = {
        ...state.medidasData[name],
        loading,
      }
    },

    setOpcionesMedidas: (
      state,
      action: PayloadAction<
        Partial<SearchNeumaticosMedidasStateType['medidasData']>
      >
    ) => {
      for (const key of Object.keys(action.payload) as MedidaName[]) {
        if (!state.medidasData[key]) continue

        const merged = {
          ...state.medidasData[key],
          ...action.payload[key],
        }
        const options = merged.options ?? []
        const value = merged.value
        if (
          !merged.loading &&
          value != null &&
          value !== '' &&
          (options.length === 0 ||
            !options.some((o) => String(o.value) === String(value)))
        ) {
          merged.value = null
          state.selectedMedidas[key] = null
        }

        state.medidasData[key] = merged
      }

      if (!state.deferMedidasCatalogConfirmUntilApply) {
        syncIsMedidasConfirmedWithSelection(state)
      }
    },

    restorePreviousMedidas: (state) => {
      applyPreviousMedidasToState(state)
      syncStateAfterCatalogMedidasRestore(state)
    },

    abortCatalogMedidasEdit: (state) => {
      applyPreviousMedidasToState(state)
      syncStateAfterCatalogMedidasRestore(state)
    },

    setDisabledEstaciones: (state, action: PayloadAction<Temporada[]>) => {
      state.disabledEstaciones = action.payload
    },

    setMedidasConfirmed: (state, action: PayloadAction<boolean>) => {
      state.isMedidasConfirmed = action.payload
      if (action.payload) {
        applyMedidasAlteredOnCatalogWithVehicleSync(state)
      }
    },
    setDeferMedidasCatalogConfirmUntilApply: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.deferMedidasCatalogConfirmUntilApply = action.payload
    },
    setSelectedMatriculaMeasureId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedMatriculaMeasureId = action.payload
    },
    setMatriculaMedidasLoading: (state) => {
      state.matriculaMedidasLoading = true
      state.matriculaMedidasError = null
      state.matriculaMedidas = []
      state.selectedMatriculaMeasureId = null
    },
    setMatriculaMedidasSuccess: (
      state,
      action: PayloadAction<MatriculaMedidasItem[]>
    ) => {
      state.matriculaMedidasLoading = false
      state.matriculaMedidasError = null
      state.matriculaMedidas = action.payload
      if (selectedMedidasArePartial(state.selectedMedidas)) {
        state.vehicleTyreSelectionWasPartial = true
      }
      applyMedidasAlteredOnCatalogWithVehicleSync(state)
    },
    setMatriculaMedidasError: (state, action: PayloadAction<string>) => {
      state.matriculaMedidasLoading = false
      state.matriculaMedidasError = action.payload
      state.matriculaMedidas = []
      state.vehicleTyreSelectionWasPartial = false
    },
    clearMatriculaMedidas: (state) => {
      state.matriculaMedidasLoading = false
      state.matriculaMedidasError = null
      state.matriculaMedidas = []
      state.selectedMatriculaMeasureId = null
      state.vehicleTyreSelectionWasPartial = false
    },
    /** Dispara la saga que refresca opciones via /tyres-with-values (medidas parcials). */
    refreshMedidasOpciones: () => {},
    /** Obre un desplegable: consulta tyres-with-values només amb mides anteriors al camp. */
    fetchMedidaOpciones: (_state, _action: PayloadAction<MedidaName>) => {},
    /** Al entrar al catàleg: reaplica defaults AMI si el vehicle ja estava carregat. */
    ensureMatriculaMedidasDefaults: () => {},
    /** Copia mides completes del funnel legacy al catàleg (p. ex. mobile des de home). */
    syncLegacyFunnelMedidasToCatalog: (
      _state,
      _action: PayloadAction<{ force?: boolean } | undefined>
    ) => {},
    /** Copia mides completes del catàleg al selector legacy (p. ex. resultados → /selector/mides). */
    syncCatalogMedidasToLegacy: () => {},
    syncMedidasAlteredOnCatalogWithVehicle: (state) => {
      applyMedidasAlteredOnCatalogWithVehicleSync(state)
    },
    setPendingAutoFocusNextMedida: (state, action: PayloadAction<boolean>) => {
      state.pendingAutoFocusNextMedida = action.payload
    },
  },
})

export const {
  setPreviousSelectedMedidasSnapshot,
  setMedidasAlteredOnCatalog,
  setVehicleTyreSelectionWasPartial,
  syncMedidasAlteredOnCatalogWithVehicle,
  updateMedidaSeleccionada,
  setEstacionSeleccionada,
  setEstacionSeleccionadaPorUsuario,
  setEstacionAutoAjustada,
  setMarcaFilterOptOut,
  resetEstacionCatalogState,
  resetMedidasSeleccionadas,
  setMedidasIniciales,
  setPageMedidas,
  setMedidaLoading,
  setOpcionesMedidas,
  restorePreviousMedidas,
  abortCatalogMedidasEdit,
  setDisabledEstaciones,
  setMedidasConfirmed,
  setDeferMedidasCatalogConfirmUntilApply,
  setMatriculaMedidasLoading,
  setMatriculaMedidasSuccess,
  setMatriculaMedidasError,
  clearMatriculaMedidas,
  refreshMedidasOpciones,
  fetchMedidaOpciones,
  ensureMatriculaMedidasDefaults,
  syncLegacyFunnelMedidasToCatalog,
  syncCatalogMedidasToLegacy,
  setSelectedMatriculaMeasureId,
  setPendingAutoFocusNextMedida,
} = medidasSlice.actions

export default medidasSlice.reducer
